/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { GyroAssistant, GyroClient, usageCapture } from '@exante/gyro-react';
import { adminScreenFor } from './lib/usageCapture';
import { api, type AdminUser } from './lib/api';
import { Overview } from './screens/Overview';
import { Configuration } from './screens/Configuration';
import { Voice } from './screens/Voice';
import { Apps } from './screens/Apps';
import { Skills } from './screens/Skills';
import { CompanySkills } from './screens/CompanySkills';
import { Activity } from './screens/Activity';
import { Analytics } from './screens/Analytics';
import { Governance } from './screens/Governance';
import { Pets } from './screens/Pets';
import { Outfits } from './screens/Outfits';
import { Access } from './screens/Access';
import { Knowledge } from './screens/Knowledge';
import { UsageCaps } from './screens/UsageCaps';
import { Adoption } from './screens/Adoption';
import { Support } from './screens/Support';
import { OfficeInstalls } from './screens/OfficeInstalls';
import { PcSwitches } from './screens/PcSwitches';
import { PulsePage } from './screens/pulse/PulsePage';
import { officeInstallsTab, officeInstallsVisible } from './lib/governance';
import { pcSwitchesNav, pcSwitchesVisible } from './lib/pcSwitches';
import { pulseNav, pulseVisible } from './lib/pulse';
import { FleetGate } from './screens/fleet/FleetPage'; import { useFleetNav } from './lib/fleet';
import { TrendsGate } from './screens/trends/TrendsPage'; import { useTrendsNav } from './lib/trends';
import { LabWidgetBoundary } from './components/LabWidgetBoundary';
import { guideLocationFor, loginUrlFor } from './lib/deepLink';
import { Shell } from './shell/Shell';
import { navigate, usePlace } from './shell/router';
import { canonicalLocation, GROUP_LABELS, navGroupFor, navGroups, navItemFor, pageFor, pageTitle, pathFor, PAGES, type PageId } from './shell/routes';
import { useTheme } from './shell/theme';
import { EmptyState, LinkButton, PageFrameProvider, PageHeader, Skeleton, type PageFrame } from './ui';
import { Button } from './ui/Button';

// [The Lab] Centralized testing widget (Lab repo VISION §3.1) — the flask pill / tester sidecar.
// Loaded LAZILY on purpose: the vendored kit is its own widget tree (+ msal-browser behind a further
// dynamic import), which as a static import would land in the admin's initial chunk. As a lazy chunk it
// is fetched only after the signed-in shell renders. See components/GyroAdminLabWidget.tsx.
const GyroAdminLabWidget = lazy(() => import('./components/GyroAdminLabWidget'));

/** A page with no screen of its own (a probe's answer, an empty state) under the shell's page header. */
function Plain({ children }: { children: ReactNode }) {
  return (
    <>
      <PageHeader />
      <div className="ui-page">{children}</div>
    </>
  );
}

/** The signed-out and not-authorized pages: one centred card, outside the shell. */
function Gate({ children }: { children: ReactNode }) {
  return (
    <div className="ui-gate">
      <div className="ui-gate-card">{children}</div>
    </div>
  );
}

const LEFT_FOR_GUIDE_KEY = 'gyro-admin:left-for-guide';
const LEFT_FOR_GUIDE_LOOP_MS = 10_000;

/**
 * The guide location to leave the SPA for, or null. The console never owns a /guide path: the server serves the guide
 * to every signed-in employee, so a browser that reached the SPA there (an older build's fallback, a deep link the
 * server did not own) is sent back to the server for the same path instead of meeting the admin sign-in gate. If this
 * very location was left for moments ago, the server is answering /guide with the console itself: stop rather than loop.
 */
function guideToLeaveFor(): string | null {
  const target = guideLocationFor(location.pathname, location.search);
  if (!target) return null;
  try {
    const previous = window.sessionStorage.getItem(LEFT_FOR_GUIDE_KEY);
    if (previous) {
      const [at, url] = previous.split(' ', 2);
      if (url === target && Date.now() - Number(at) < LEFT_FOR_GUIDE_LOOP_MS) return null;
    }
    window.sessionStorage.setItem(LEFT_FOR_GUIDE_KEY, `${Date.now()} ${target}`);
  } catch { /* no storage: leave anyway */ }
  return target;
}

export function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [authConfigured, setAuthConfigured] = useState(true);
  const place = usePlace();
  const theme = useTheme();
  // Decided once, at mount, before any effect can rewrite the address: a /guide path belongs to the server.
  const [leavingFor] = useState(guideToLeaveFor);
  useEffect(() => {
    if (leavingFor) location.replace(leavingFor);
  }, [leavingFor]);
  // Auditor-only sessions (no admin) get read access to Activity/Analytics/Adoption/Governance-style
  // GET surfaces; mutating calls 403 server-side regardless of what the UI shows.
  const auditorOnly = Boolean(user && !user.isAdmin && user.isAuditor);
  // Same-origin client (gyro-svc serves the admin); the admin SSO cookie authenticates the calls.
  const gyroClient = useMemo(() => new GyroClient({ baseUrl: '', app: 'admin' }), []);
  const [petCapability, setPetCapability] = useState<{ user: AdminUser; enabled: boolean } | null>(null);
  const petsAvailable = petCapability?.user === user && petCapability?.enabled === true;
  const petsChecking = Boolean(user && petCapability?.user !== user);
  useEffect(() => {
    if (!user || !user.isAdmin && !user.isAuditor) return;
    let current = true;
    gyroClient.status().then(
      status => { if (current) setPetCapability({ user, enabled: status.petsEnabled === true }); },
      () => { if (current) setPetCapability({ user, enabled: false }); },
    );
    return () => { current = false; };
  }, [gyroClient, user]);
  // The Office installs screen exists only where the service runs the Office add-in: its list route answers 404
  // elsewhere, and admins only (auditors get 403). Probed once per admin session; the probe's answer is null until it
  // gives one, so the tab is `checking` (a loading page on a deep link, never "not enabled") until then.
  const [officeInstallsProbe, setOfficeInstallsProbe] = useState<boolean | null>(null);
  const isAdmin = Boolean(user?.isAdmin);
  const officeInstalls = officeInstallsTab(isAdmin, officeInstallsProbe);
  useEffect(() => {
    if (!isAdmin) return;
    let live = true;
    setOfficeInstallsProbe(null);
    api.officeInstalls().then(
      () => live && setOfficeInstallsProbe(true),
      (error) => live && setOfficeInstallsProbe(officeInstallsVisible(error)),
    );
    return () => {
      live = false;
    };
  }, [isAdmin]);

  // Operations › PC switches exist only where the service has them switched on: GET /v1/admin/desktop/policy answers 404
  // elsewhere, and admins and auditors may read it (auditors see the page read-only). Probed once per console session;
  // the answer is null until it gives one, so a deep link shows `checking`, never "not switched on", meanwhile.
  const [pcSwitchesProbe, setPcSwitchesProbe] = useState<boolean | null>(null);
  const consoleRole = Boolean(user && (user.isAdmin || user.isAuditor));
  const pcSwitches = pcSwitchesNav(consoleRole, pcSwitchesProbe);
  useEffect(() => {
    if (!consoleRole) return;
    let live = true;
    setPcSwitchesProbe(null);
    api.desktopPolicy().then(
      () => live && setPcSwitchesProbe(true),
      (error) => live && setPcSwitchesProbe(pcSwitchesVisible(error)),
    );
    return () => {
      live = false;
    };
  }, [consoleRole]);

  // Pulse exists only where the service has the pulse flag on: GET /v1/admin/pulse/settings answers 404 `pulse_disabled`
  // elsewhere (docs/CONTRACT-pulse.md section 1), and admins and auditors may read it (auditors see the pages read-only).
  // Probed once per console session, the Office-installs pattern above; a deep link shows `checking` until it answers.
  const [pulseProbe, setPulseProbe] = useState<boolean | null>(null);
  const pulse = pulseNav(consoleRole, pulseProbe);
  useEffect(() => {
    if (!consoleRole) return;
    let live = true;
    setPulseProbe(null);
    api.pulseSettings().then(
      () => live && setPulseProbe(true),
      (error) => live && setPulseProbe(pulseVisible(error)),
    );
    return () => {
      live = false;
    };
  }, [consoleRole]);
  const fleet = useFleetNav(consoleRole);
  const trends = useTrendsNav(consoleRole);

  // A link written before the router (`/?tab=support&request=<id>`) becomes the page's own path, query kept.
  useEffect(() => {
    if (leavingFor) return;
    const canonical = canonicalLocation(window.location.pathname, window.location.search);
    if (canonical) navigate(canonical, { replace: true });
  }, [leavingFor]);

  useEffect(() => {
    api
      .me()
      .then((r) => {
        setUser(r.user);
        if (r.authConfigured === false) setAuthConfigured(false);
        // Auditors land on Activity (a read they're allowed) instead of the Overview (admin-only API).
        if (r.user && !r.user.isAdmin && r.user.isAuditor) navigate(pathFor('activity'), { replace: true });
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const page: PageId = pageFor(place.pathname, place.search, 'dashboard');
  // The usage record: the page on view (`admin:<page>`, a Fleet health or Trends view, a Pulse tab) and the signed-in
  // account as the queue's owner, keyed as the console's own Gyro widget keys it (the user's id); a sign-out flushes that
  // account's queue, then drops it.
  useEffect(() => {
    usageCapture().screen(adminScreenFor(page, place.search));
  }, [page, place.search]);
  const userKey = user ? user.id || user.email : null;
  useEffect(() => {
    usageCapture().setUser(userKey);
  }, [userKey]);
  const signOut = () => void usageCapture().signOut().then(() => api.logout()).then(() => location.reload());
  const groups = useMemo(
    () => navGroups({ officeInstalls: officeInstalls === 'shown', pets: petsAvailable, pcSwitches: pcSwitches === 'shown', pulse: pulse === 'shown', fleet: fleet === 'shown', trends: trends === 'shown' }),
    [officeInstalls, petsAvailable, pcSwitches, pulse, fleet, trends],
  );
  const frame = useMemo<PageFrame>(() => {
    if (page === 'pets' && !petsAvailable) {
      const title = petsChecking ? 'Checking page availability' : 'Page not found';
      return { crumbs: [title], title, activeTab: page };
    }
    const item = navItemFor(page, groups);
    const group = navGroupFor(page, groups);
    const title = pageTitle(page, groups);
    return {
      crumbs: [group?.label ?? GROUP_LABELS[PAGES[page].group], title],
      title,
      tabs: item && item.pages.length > 1 ? item.pages.map((id) => ({ id, label: PAGES[id].label, href: pathFor(id) })) : undefined,
      activeTab: page,
    };
  }, [page, groups, petsAvailable, petsChecking]);

  // A signed-in employee who is neither admin nor auditor is not in the console: its name stays off their tab.
  const consoleUser = !user || user.isAdmin || Boolean(user.isAuditor);
  useEffect(() => {
    document.title = consoleUser ? `${frame.title} · Gyro Admin` : 'Gyro';
  }, [frame.title, consoleUser]);

  if (leavingFor) return null;

  if (loading) {
    return (
      <div className="ui-gate" aria-busy="true">
        <span className="ui-sr">Loading Gyro Admin</span>
        <Skeleton size="xl" width="160px" />
      </div>
    );
  }

  if (!user) {
    return (
      <Gate>
        <img className="ui-gate-logo" src={theme.resolved === 'dark' ? '/brand/exante360-logo-reversed.png' : '/brand/exante360-logo.png'} alt="Exante360" />
        <h1 className="ui-gate-title">Gyro Admin</h1>
        <p className="ui-gate-text">Central control for the Gyro assistant.</p>
        {authConfigured ? (
          <LinkButton variant="primary" className="ui-gate-action" href={loginUrlFor(location.pathname, location.search)}>Sign in with Microsoft</LinkButton>
        ) : (
          <p className="ui-gate-note is-warn">SSO isn&apos;t configured on the service yet (ENTRA_* env). See the deployment docs.</p>
        )}
        <p className="ui-gate-foot">Sign in with your Exante360 work account.</p>
      </Gate>
    );
  }

  // Signed in, not IT staff: the user guide is theirs (owner, 2026-10-11), the console is not. A plain link, so the
  // browser leaves for the server's guide; the IT-admin requirement itself is enforced server-side (/v1/admin).
  if (!user.isAdmin && !user.isAuditor) {
    return (
      <Gate>
        <img className="ui-gate-logo" src={theme.resolved === 'dark' ? '/brand/exante360-logo-reversed.png' : '/brand/exante360-logo.png'} alt="Exante360" />
        <h1 className="ui-gate-title">{`You're signed in as ${user.displayName}.`}</h1>
        <p className="ui-gate-text">The admin console is for IT staff.</p>
        <LinkButton variant="primary" className="ui-gate-action" href="/guide">Open the user guide</LinkButton>
        <Button icon={LogOut} variant="ghost" className="ui-gate-action" onClick={signOut}>Sign out</Button>
      </Gate>
    );
  }

  let content: ReactNode = null;
  switch (page) {
    case 'dashboard': content = <Overview />; break;
    case 'adoption': content = <Adoption />; break;
    case 'analytics': content = <Analytics canEdit={user.isAdmin} />; break;
    case 'activity': content = <Activity view="traces" />; break;
    case 'audit': content = <Activity view="audit" />; break;
    case 'approvals': content = <Activity view="approvals" petsAvailable={petsAvailable} canModeratePets={user.isAdmin} />; break;
    case 'config': content = <Configuration />; break;
    case 'caps': content = <UsageCaps />; break;
    case 'voice': content = <Voice />; break;
    case 'apps': content = <Apps />; break;
    case 'access': content = <Access />; break;
    case 'knowledge': content = <Knowledge />; break;
    case 'support': content = <Support />; break;
    case 'skills': content = <Skills />; break;
    case 'company-skills': content = <CompanySkills />; break;
    case 'governance': content = <Governance />; break;
    case 'outfits': content = <Outfits />; break;
    case 'pets': content = petsAvailable ? <Pets canModerate={user.isAdmin} />
      : <Plain><EmptyState title={petsChecking ? 'Checking page availability' : 'Page not found'}>
        {petsChecking ? 'Checking whether this page is available for your account.' : 'This page is not available.'}
      </EmptyState></Plain>; break;
    case 'office-installs':
      content = officeInstalls === 'checking'
        ? <Plain><EmptyState title="Checking whether the Office add-in is enabled">The Office installs list appears here once the service answers.</EmptyState></Plain>
        : officeInstalls === 'shown'
          ? <OfficeInstalls />
          : <Plain><EmptyState title="The Office add-in is not enabled">This service does not run the Office add-in, so there are no Office installs to show.</EmptyState></Plain>;
      break;
    case 'pc-switches':
      content = pcSwitches === 'checking'
        ? <Plain><EmptyState title="Checking whether PC switches are on">The PC switches appear here once the service answers.</EmptyState></Plain>
        : pcSwitches === 'shown'
          ? <PcSwitches canEdit={user.isAdmin} me={{ email: user.email, name: user.displayName }} />
          : <Plain><EmptyState title="PC switches are not switched on">This service does not offer PC switches yet, so every PC follows its IT setting.</EmptyState></Plain>;
      break;
    case 'pulse':
    case 'pulse-asks':
    case 'pulse-domains':
    case 'pulse-tools':
    case 'pulse-feedback':
    case 'pulse-problems':
    case 'pulse-digests':
    case 'pulse-operations':
    case 'pulse-settings':
      content = pulse === 'checking'
        ? <Plain><EmptyState title="Checking whether Pulse is on">The Pulse pages appear here once the service answers.</EmptyState></Plain>
        : pulse === 'shown'
          ? <PulsePage page={page} canEdit={user.isAdmin} />
          : <Plain><EmptyState title="Pulse is not switched on">Pulse records what people ask Gyro and whether it helped, without recording who asked. It appears here once the pulse flag is turned on under Feature flags.</EmptyState></Plain>;
      break;
    case 'fleet': content = <FleetGate state={fleet} canEdit={user.isAdmin} />; break;
    case 'trends': content = <TrendsGate state={trends} canEdit={user.isAdmin} />; break;
  }

  return (
    <>
      <PageFrameProvider value={frame}>
        {/* key: a page change mounts the new screen fresh, as the tabs always did. */}
        <Shell
          user={user}
          auditorOnly={auditorOnly}
          page={page}
          groups={groups}
          theme={theme}
          onSignOut={signOut}
        >
          <div key={page} className="shell-page-frame">{content}</div>
        </Shell>
      </PageFrameProvider>

      {/* Gyro's panel follows the console's light or dark. */}
      <GyroAssistant client={gyroClient} user={{ id: user.id, email: user.email }} title="Gyro" appKey="gyro-admin" theme={theme.resolved} />

      {/* [The Lab] Testing widget — signed-in admins/auditors only (we are past both gates here). It is a
          self-hiding fixed overlay behind its own error boundary: if the Lab API is unreachable, the user is
          unknown to the Lab, the chunk fails to load, or the widget crashes, it renders nothing and the
          console is unaffected (resilience contract, VISION §3.1). No feature flag: the admin has no
          client-side flag system, and the widget hides itself for anyone the Lab has not enrolled. */}
      <LabWidgetBoundary>
        <Suspense fallback={null}>
          <GyroAdminLabWidget user={user} />
        </Suspense>
      </LabWidgetBoundary>
    </>
  );
}
